/// <amd-module name="Analytics/Shared/Visuals/ColumnChartVisual/Components/ColumnChartVisual" />
import * as Resources from "Analytics/Shared/Visuals/ColumnChartVisual/Resources";
import { IVisualTemplateProps, VisualTemplate } from "Analytics/Shared/Visuals/Common/Components/VisualTemplate";
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { Chart } from "VSS/Features/Charts/Chart";
import { ChartProps } from "VSS/Features/Charts/Contracts";
import { css, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export interface IColumnChartLabelledMenuProps {
    tooltipHostContent?: string;
    contextualMenuAriaLabel?: string;
    metricOptions?: { [key: number]: string };
    selectedMetricOption?: number;
    onMetricChange?: (metricOptionSelected: number) => void;
}
export interface IColumnChartVisualProps extends IVssComponentProperties {
    columnChartLabelledMenuProps?: IColumnChartLabelledMenuProps;
    chartOptions?: ChartProps;
    title?: string;
    titleClassName?: string;
    chartContainerClassName?: string;
    errorText?: string;
    errorTextRenderer?: (errorText?: string) => JSX.Element;
    useBoltCard?: boolean;
}

/** ColumnChartVisual allows the ColumnLine and StackedColumn type charts.
 *  ColumnChartVisual creates visualTemplate for allowed type of charts.
 */

export class ColumnChartVisual extends VssComponent<IColumnChartVisualProps> {
    public render(): JSX.Element {
        if (!this.props.useBoltCard) {
            return (
                <VisualTemplate
                    className={css(this.props.className || "", "column-chart-visual")}
                    {...this._getVisualTemplateProps()}
                    style={this.props.style}
                    title={this.props.title || ""}
                    titleClassName={this.props.titleClassName || ""}
                >
                    {this._getContent()}
                </VisualTemplate>
            );
        } else {
            return (
                <Card className={this.props.className} titleProps={{ text: this.props.title }}>
                    {this._getContent()}
                </Card>
            );
        }
    }

    private _getContent(): JSX.Element | null {
        if (this.props.errorText) {
            return this._renderError();
        }

        if (!this.props.chartOptions && !this.props.errorText) {
            return <Spinner className="analytics-card-spinner flex-grow flex-column" size={SpinnerSize.large} />;
        }

        if (this.props.chartOptions) {
            return (
                <div className={css("flex-column", this.props.chartContainerClassName || "")} style={this.props.style}>
                    <Chart {...this.props.chartOptions} className="flex-column" style={this.props.style} />
                </div>
            );
        }

        return null;
    }

    private _renderError(): JSX.Element | null {
        if (this.props.errorText) {
            if (this.props.errorTextRenderer) {
                return this.props.errorTextRenderer(this.props.errorText);
            } else {
                return <div className="column-chart-error">{this.props.errorText}</div>;
            }
        } else {
            return null;
        }
    }

    private _getChartProps(): IColumnChartVisualProps {
        return {
            chartOptions: this.props.chartOptions
        };
    }

    private _getVisualTemplateProps(): IVisualTemplateProps {
        const visualTemplateProps: IVisualTemplateProps = {
            className: this.props.className
        };

        if (this.props.columnChartLabelledMenuProps) {
            const labelledMenuProps: IColumnChartLabelledMenuProps = this.props.columnChartLabelledMenuProps;
            visualTemplateProps.labelledMenuProps = {
                tooltipHostContent: labelledMenuProps.tooltipHostContent || Resources.ColumnChartLabel,
                contextualMenuAriaLabel: labelledMenuProps.contextualMenuAriaLabel || this._getContextualMenuAriaLabel(),
                metricOptions: labelledMenuProps.metricOptions,
                selectedMetricOption: labelledMenuProps.selectedMetricOption,
                onMetricChange: labelledMenuProps.onMetricChange
            };
        }
        return visualTemplateProps;
    }

    private _getContextualMenuAriaLabel() {
        if (this.props.columnChartLabelledMenuProps) {
            const metricOptions = this.props.columnChartLabelledMenuProps.metricOptions;
            const selectedMetricOption = this.props.columnChartLabelledMenuProps.selectedMetricOption;
            if (metricOptions && selectedMetricOption) {
                return format(Resources.ColumnChartMetricSelectedAriaLabel, metricOptions[selectedMetricOption]);
            }
        }
        return "";
    }
}
